import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDateTime } from "@/lib/format";
import { ProductCombobox } from "@/components/ProductCombobox";
import { SlidersHorizontal, Plus, Trash2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const REASONS = ["Damage / breakage", "Theft / loss", "Expiry write-off", "Count correction", "Found stock", "Supplier shortfall", "Other"];
const emptyLine = () => ({ product_id: "", batch_no: "", expiry_date: "", quantity: "", reason: "Count correction" });

export default function Adjustments() {
  const { can } = useAuth();
  const { branches } = useBranch();
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [branchId, setBranchId] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [detail, setDetail] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(() => api.get("/adjustments").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, [load]);

  const openNew = () => { setBranchId(branches[0]?.id || ""); setNote(""); setLines([emptyLine()]); setOpen(true); };
  const setLine = (i, patch) => setLines((a) => a.map((l, idx) => idx === i ? { ...l, ...patch } : l));

  const submit = async () => {
    if (!branchId) return toast.error("Select a branch");
    const payload = lines
      .filter((l) => l.product_id && l.batch_no && l.expiry_date && Number(l.quantity) !== 0)
      .map((l) => ({ product_id: l.product_id, batch_no: l.batch_no, expiry_date: l.expiry_date, quantity: Number(l.quantity), reason: l.reason }));
    if (!payload.length) return toast.error("Add at least one line with product, batch, expiry and a non-zero quantity");
    try { const { data } = await api.post("/adjustments", { branch_id: branchId, note, lines: payload });
      toast.success(`${data.code} raised — awaiting approval`); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const approve = async (a) => {
    if (!window.confirm(`Approve ${a.code} and post ${a.lines.length} stock change(s)?`)) return;
    try { const { data } = await api.post(`/adjustments/${a.id}/approve`); toast.success(`Approved — ${data.applied} line(s) posted`); setDetail(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doReject = async () => {
    try { await api.post(`/adjustments/${rejectFor.id}/reject`, { reason: rejectReason }); toast.success("Adjustment rejected"); setRejectFor(null); setRejectReason(""); setDetail(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const badge = (s) => s === "approved" ? "ok" : s === "rejected" ? "out" : "pending";

  return (
    <div>
      <PageHeader title="Stock Adjustments" subtitle="Raise batch-level stock corrections (single or multiple products). Approval by Super Admin or Accountant."
        actions={<Button data-testid="new-adjustment-btn" onClick={openNew}><SlidersHorizontal className="h-4 w-4 mr-1.5" /> New adjustment</Button>} />

      <Card className="shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr><th className="py-2.5 px-3">Code</th><th className="py-2.5 px-3">Branch</th><th className="py-2.5 px-3 text-right">Lines</th><th className="py-2.5 px-3">Status</th><th className="py-2.5 px-3">Raised by</th><th className="py-2.5 px-3">When</th><th className="py-2.5 px-3"></th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-muted-foreground"><SlidersHorizontal className="h-8 w-8 mx-auto mb-2 opacity-40" />No adjustments yet</td></tr>}
            {rows.map((a) => (
              <tr key={a.id} className="border-t border-border hover:bg-muted/50 cursor-pointer" data-testid={`adj-row-${a.code}`} onClick={() => setDetail(a)}>
                <td className="py-2 px-3 font-mono text-xs">{a.code}</td>
                <td className="py-2 px-3">{a.branch_name}</td>
                <td className="py-2 px-3 text-right font-mono">{a.lines.length}</td>
                <td className="py-2 px-3"><StatusBadge value={badge(a.status)} label={a.status} /></td>
                <td className="py-2 px-3 text-muted-foreground">{a.created_by}</td>
                <td className="py-2 px-3 text-muted-foreground text-xs">{fmtDateTime(a.created_at)}</td>
                <td className="py-2 px-3 text-right"><Button size="sm" variant="ghost" data-testid={`view-adj-${a.code}`} onClick={(e) => { e.stopPropagation(); setDetail(a); }}>View</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* New adjustment */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New stock adjustment</DialogTitle><DialogDescription>Each line targets a specific batch (batch no + expiry). Use + to add stock, − to remove.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Branch</Label>
                <Select value={branchId} onValueChange={setBranchId}>
                  <SelectTrigger data-testid="adj-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Note (optional)</Label><Input data-testid="adj-note-input" value={note} onChange={(e) => setNote(e.target.value)} /></div>
            </div>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end border border-border rounded-lg p-2">
                  <div className="col-span-12"><Label className="text-xs">Product</Label>
                    <ProductCombobox products={products} value={l.product_id} onChange={(v) => setLine(i, { product_id: v })} testId={`adj-product-${i}`} />
                  </div>
                  <div className="col-span-3"><Label className="text-xs">Batch no.</Label><Input className="h-9" data-testid={`adj-batch-${i}`} value={l.batch_no} onChange={(e) => setLine(i, { batch_no: e.target.value })} /></div>
                  <div className="col-span-3"><Label className="text-xs">Expiry</Label><Input type="date" className="h-9" data-testid={`adj-expiry-${i}`} value={l.expiry_date} onChange={(e) => setLine(i, { expiry_date: e.target.value })} /></div>
                  <div className="col-span-2"><Label className="text-xs">Qty ±</Label><Input type="number" className="h-9" placeholder="e.g. -5" data-testid={`adj-qty-${i}`} value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} /></div>
                  <div className="col-span-3"><Label className="text-xs">Reason</Label>
                    <Select value={l.reason} onValueChange={(v) => setLine(i, { reason: v })}>
                      <SelectTrigger className="h-9" data-testid={`adj-reason-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1 flex justify-end"><button className="text-muted-foreground hover:text-rose-600 pb-2" data-testid={`adj-remove-${i}`} onClick={() => setLines((a) => a.filter((_, idx) => idx !== i))}><Trash2 className="h-4 w-4" /></button></div>
                </div>
              ))}
            </div>
            <Button size="sm" variant="outline" data-testid="adj-add-line" onClick={() => setLines((a) => [...a, emptyLine()])}><Plus className="h-3.5 w-3.5 mr-1" /> Add line</Button>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="submit-adjustment-btn" onClick={submit}>Submit for approval</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / approve */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2">{detail?.code} · {detail?.branch_name} <StatusBadge value={badge(detail?.status)} label={detail?.status} /></DialogTitle>
            <DialogDescription>Raised by {detail?.created_by} · {detail && fmtDateTime(detail.created_at)}{detail?.note ? ` · ${detail.note}` : ""}</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="overflow-x-auto border-y border-border">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground text-left"><tr><th className="py-2 px-2">Product</th><th className="py-2 px-2">Batch</th><th className="py-2 px-2">Expiry</th><th className="py-2 px-2 text-right">Qty ±</th><th className="py-2 px-2">Reason</th></tr></thead>
                <tbody>
                  {detail.lines.map((l, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="py-1.5 px-2">{l.generic_name}</td>
                      <td className="py-1.5 px-2 font-mono text-xs">{l.batch_no}</td>
                      <td className="py-1.5 px-2 text-xs">{l.expiry_date}</td>
                      <td className={`py-1.5 px-2 text-right font-mono ${l.quantity < 0 ? "text-rose-600" : "text-emerald-600"}`}>{l.quantity > 0 ? `+${l.quantity}` : l.quantity}</td>
                      <td className="py-1.5 px-2 text-xs text-muted-foreground">{l.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {detail?.status === "rejected" && detail?.reject_reason && <p className="text-sm text-rose-600">Rejected: {detail.reject_reason}</p>}
          {detail?.status === "approved" && <p className="text-sm text-emerald-600">Approved by {detail.approved_by} · {fmtDateTime(detail.approved_at)}</p>}
          {detail?.status === "pending" && can("adjustment.approve") && (
            <DialogFooter className="gap-2">
              <Button variant="outline" className="text-rose-600 border-rose-300" data-testid="reject-adjustment-btn" onClick={() => setRejectFor(detail)}><XCircle className="h-4 w-4 mr-1.5" /> Reject</Button>
              <Button data-testid="approve-adjustment-btn" onClick={() => approve(detail)}><CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve & post</Button>
            </DialogFooter>
          )}
          {detail?.status === "pending" && !can("adjustment.approve") && <p className="text-xs text-muted-foreground">Awaiting approval by a Super Admin or Accountant.</p>}
        </DialogContent>
      </Dialog>

      {/* Reject reason */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Reject {rejectFor?.code}</DialogTitle></DialogHeader>
          <div><Label>Reason</Label><Input data-testid="adj-reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} /></div>
          <DialogFooter><Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button><Button className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-reject-adjustment-btn" onClick={doReject}>Confirm rejection</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
